/**
 * 12-Week First Mass (Beginner Body Composition)
 *
 * For users who pick the `body-comp` goal at onboarding AND tag
 * themselves as `beginner`. The intermediate Hypertrophy Builder runs
 * 5 days/week with an upper/lower split that assumes the lifter can
 * already squat, hinge, press, and row with confidence. A true novice
 * doing that program risks hating the gym and quitting.
 *
 * Methodology — Full-Body 3×/Week, the most evidence-supported
 * approach for novice hypertrophy and recomposition. The principle:
 * frequency drives motor learning faster than per-session volume, and
 * a beginner's MRV (maximum recoverable volume) is low enough that
 * 3 short full-body sessions produce more growth than 5 or 6 specialized
 * ones. As volume tolerance grows the lifter graduates to the upper/
 * lower split (Hypertrophy Builder).
 *
 * Structure:
 * - 5 days/week: 3 full-body lifting + 1 easy cardio + 1 mobility/walk
 * - Same template (slightly varied) across the 3 lifting days — high
 *   frequency on the same patterns is exactly what builds the motor
 *   skills and the muscle simultaneously
 * - Beginner-friendly exercises: Goblet Squat (not Back Squat),
 *   Romanian Deadlift (not Deadlift), Dumbbell Bench (not Barbell),
 *   Inverted Row (scalable to body angle). Barbell-only programs scare
 *   novices off; dumbbells and bodyweight are how most people start.
 * - Cardio days are light Z2 — for cardiovascular health, not fat
 *   loss. The bodyCompGoal-driven calorie system handles deficit if
 *   the user wants to cut.
 */

import type { WorkoutProgram, WorkoutTemplateDay, ProgramMetadata, ProgramPeriodizationModel } from '../types/program';

const metadata: ProgramMetadata = {
  id: 'first-mass-12week',
  name: '12-Week First Mass',
  description: 'Full-body lifting three times a week for new lifters. Builds muscle, teaches the patterns, sustainable. Two light cardio days keep the heart healthy without forcing a deficit.',
  level: 'recruit',
  duration: 12,
  daysPerWeek: 5,
  goals: ['body-comp'],
  difficulty: 'beginner',
  version: '1.0.0',
  createdAt: new Date(),
  updatedAt: new Date(),
  isActive: true,
  hasRangeDay: false,
};

const periodization: ProgramPeriodizationModel = {
  style: 'linear',
  phases: [
    // W1 + W12: Testing
    {
      weeks: [1, 12],
      type: 'assessment',
      name: 'Body-Comp Testing',
      focus: 'AMRAP push-ups, max plank, light-load benchmarks on the main movements',
      intensityMultiplier: 1.0,
      volumeMultiplier: 1.0,
    },
    // Weeks 2-5: Pattern Building. Lighter loads so the lifter feels
    // every position; volume is moderate (3 sets); RPE caps at 7 so
    // form never breaks down. The point is rep practice.
    {
      weeks: [2, 3, 4, 5],
      type: 'accumulation',
      name: 'Pattern Building',
      focus: 'Light loads, RPE 7 cap, perfect reps. Learn to feel the muscle.',
      intensityMultiplier: 1.0,
      volumeMultiplier: 1.0,
      progression: {
        type: 'linear',
        startIntensity: 0.55,
        endIntensity: 0.65,
        weeklyIncrease: 0.025,
      },
      trainingParameters: {
        repRange: [10, 12],
        setsMultiplier: 1.0,
        restPeriod: '90s',
      },
    },
    // Week 6: Cutback. Loads drop, sets drop. CNS recovers.
    {
      weeks: [6],
      type: 'deload',
      name: 'Cutback Week',
      focus: 'Drop volume 30%. Recover, re-feel form, reset.',
      intensityMultiplier: 0.85,
      volumeMultiplier: 0.7,
      progression: {
        type: 'linear',
        startIntensity: 0.60,
        endIntensity: 0.60,
        weeklyIncrease: 0,
      },
      trainingParameters: {
        repRange: [10, 12],
        setsMultiplier: 0.7,
        restPeriod: '90s',
      },
    },
    // Weeks 7-10: Build Phase. Loads rise, sets stay 3, reps climb to
    // 12-15 on the second half of each session. RPE cap rises to 8.
    {
      weeks: [7, 8, 9, 10],
      type: 'accumulation',
      name: 'Build Phase',
      focus: 'Moderate loads, RPE 8 cap, more reps. The muscle-gain phase.',
      intensityMultiplier: 1.0,
      volumeMultiplier: 1.05,
      progression: {
        type: 'linear',
        startIntensity: 0.65,
        endIntensity: 0.75,
        weeklyIncrease: 0.025,
      },
      trainingParameters: {
        repRange: [10, 15],
        setsMultiplier: 1.05,
        restPeriod: '60-90s',
      },
    },
    // Week 11: Light week before the retest.
    {
      weeks: [11],
      type: 'deload',
      name: 'Light Week',
      focus: 'Half volume, maintain form. Sharpen for retest.',
      intensityMultiplier: 0.9,
      volumeMultiplier: 0.5,
      progression: {
        type: 'linear',
        startIntensity: 0.70,
        endIntensity: 0.70,
        weeklyIncrease: 0,
      },
      trainingParameters: {
        repRange: [10, 12],
        setsMultiplier: 0.5,
        restPeriod: '90s',
      },
    },
  ],
};

// BASE TEMPLATES

const testingDay: WorkoutTemplateDay = {
  name: 'Body-Comp Test',
  sessionType: 'assessment',
  estimatedDuration: 50,
  blocks: [
    {
      name: 'Movement Prep',
      type: 'prep',
      exercises: [
        { name: 'Cat-Cow Stretch', sets: 2, reps: 8, format: 'recovery', details: 'Spine mobility' },
        { name: 'Hip Opener Flow', sets: 1, reps: 10, format: 'recovery', details: 'Open the hips before squatting' },
        { name: 'Band Pull-Aparts', sets: 2, reps: 15, format: 'recovery', details: 'Wake up the upper back' },
      ],
    },
    {
      name: 'Bodyweight Markers',
      type: 'testing',
      exercises: [
        { name: 'Push-Ups', sets: 1, reps: 'AMRAP', format: 'bodyweight', details: 'Max strict reps. Knees down is fine if needed.', sessionType: 'assessment' },
        { name: 'Plank Hold', sets: 1, reps: 'Max Time', format: 'bodyweight', details: 'Stop when form breaks (hips sag).', sessionType: 'assessment' },
      ],
    },
    {
      name: 'Light Load Benchmarks',
      type: 'testing',
      exercises: [
        { name: 'Goblet Squat', sets: 1, reps: '10RM', intensity: 'Tech', details: 'Find a load you can do for 10 reps with PERFECT form.', sessionType: 'assessment' },
        { name: 'Romanian Deadlift', sets: 1, reps: '10RM', intensity: 'Tech', details: 'Hinge pattern, slight knee bend, neutral spine.', sessionType: 'assessment' },
        { name: 'Dumbbell Bench Press', sets: 1, reps: '10RM', intensity: 'Tech', details: '10 perfect reps. Use a bench with dumbbells.', sessionType: 'assessment' },
        { name: 'Dumbbell Row', sets: 1, reps: '10RM', intensity: 'Tech', details: 'One arm at a time, brace against bench.', sessionType: 'assessment' },
      ],
    },
    {
      name: 'Cooldown',
      type: 'recovery',
      exercises: [
        { name: 'Cat-Cow Stretch', sets: 2, reps: 10, format: 'recovery', details: 'Decompress' },
      ],
    },
  ],
};

// Lifting day — full body, 6 movements. Same template every lifting day
// for novice frequency. The slight rotation between Day 1/3/5 happens
// in the second half (we let users pick a curl variation, isolation
// targets) but core lifts are constant.
const fullBodyDay: WorkoutTemplateDay = {
  name: 'Full Body Lifting',
  sessionType: 'pt',
  estimatedDuration: 50,
  blocks: [
    {
      name: 'Warm-Up',
      type: 'prep',
      exercises: [
        { name: 'Cat-Cow Stretch', sets: 2, reps: 8, format: 'recovery', details: 'Wake up the spine before loading' },
        { name: 'Hip Opener Flow', sets: 1, reps: 10, format: 'recovery', details: 'Open hips for squatting and hinging' },
        { name: 'Band Pull-Aparts', sets: 2, reps: 15, format: 'recovery', details: 'Activate upper back for pressing and pulling' },
      ],
    },
    {
      name: 'Compound Lifts',
      type: 'strength',
      exercises: [
        { name: 'Goblet Squat', sets: 3, reps: 10, intensity: 'progressive', details: 'Hold a dumbbell or kettlebell at chest. Squat to depth, drive through heels. Foundation movement — earn the back squat later.' },
        { name: 'Romanian Deadlift', sets: 3, reps: 10, intensity: 'progressive', details: 'Hinge pattern with dumbbells or barbell. Slight knee bend, neutral spine, feel the hamstrings.' },
        { name: 'Dumbbell Bench Press', sets: 3, reps: 10, intensity: 'progressive', details: 'Lying on a bench with dumbbells. Controlled descent, full press. Easier on the shoulders than barbell to start.' },
        { name: 'Dumbbell Row', sets: 3, reps: 10, intensity: 'progressive', details: 'One arm at a time, brace against bench. Pull dumbbell to hip, squeeze shoulder blade.' },
      ],
    },
    {
      name: 'Isolation + Core',
      type: 'strength',
      exercises: [
        { name: 'Hip Thrust', sets: 2, reps: 12, intensity: 'RPE 7', details: 'Bench against your back, barbell or dumbbell over hips. Pause at the top, squeeze glutes hard.' },
        { name: 'Plank Hold', sets: 2, reps: '30-45s', format: 'bodyweight', details: 'Anti-extension. Brace abs, glutes squeezed, shoulders over elbows.' },
      ],
    },
    {
      name: 'Cooldown',
      type: 'recovery',
      exercises: [
        { name: 'Hip Mobility Flow', sets: 2, reps: 8, format: 'recovery', details: 'Decompress hips and spine' },
      ],
    },
  ],
};

// Easy cardio day — Z2 walking, biking, or jogging for ~20-25 min.
// Health benefit (cardiovascular adaptations + recovery), not fat-loss
// burn. Keep it easy — the lifting days are where the muscle stimulus
// happens.
const easyCardioDay: WorkoutTemplateDay = {
  name: 'Easy Cardio',
  sessionType: 'pt',
  estimatedDuration: 30,
  blocks: [
    {
      name: 'Aerobic Base',
      type: 'conditioning',
      exercises: [
        { name: 'Easy Run', duration: '20-25 min', format: 'cardio', intensity: 'Easy', details: 'Brisk walk, easy bike, or slow jog — whatever doesn\'t leave you sore. Conversational pace, RPE 5.' },
      ],
    },
    {
      name: 'Cooldown',
      type: 'recovery',
      exercises: [
        { name: 'Hip Opener Flow', sets: 1, reps: 10, format: 'recovery', details: 'Open the hips and stretch tight calves' },
      ],
    },
  ],
};

// Mobility / active recovery day. Optional intensity — users who feel
// great on this day can stack a longer walk; users who're cooked just
// do the mobility flow.
const mobilityDay: WorkoutTemplateDay = {
  name: 'Mobility + Active Recovery',
  sessionType: 'pt',
  estimatedDuration: 25,
  blocks: [
    {
      name: 'Full-Body Mobility',
      type: 'recovery',
      exercises: [
        { name: 'Cat-Cow Stretch', sets: 3, reps: 10, format: 'recovery', details: 'Slow spine articulation' },
        { name: 'Hip Opener Flow', sets: 3, reps: 10, format: 'recovery', details: 'Hips, glutes, calves' },
        { name: 'Band Pull-Aparts', sets: 3, reps: 15, format: 'recovery', details: 'Posture insurance — fights the desk-worker slump' },
        { name: 'Plank Hold', sets: 2, reps: '30s', format: 'bodyweight', details: 'Light core endurance' },
      ],
    },
  ],
};

const restDay: WorkoutTemplateDay = {
  name: 'Rest Day',
  sessionType: 'pt',
  estimatedDuration: 0,
  blocks: [],
};

export const firstMass12Week: WorkoutProgram = {
  metadata,
  periodization,
  templates: {
    // Testing weeks (W1, W12)
    'testing-week-day1': testingDay,
    'testing-week-day2': restDay,
    'testing-week-day3': restDay,
    'testing-week-day4': restDay,
    'testing-week-day5': restDay,
    'testing-week-day6': restDay,
    'testing-week-day7': restDay,

    // Training cycle — Mon/Wed/Fri lifting, Tue cardio, Sat mobility
    'training-day1': fullBodyDay,
    'training-day2': easyCardioDay,
    'training-day3': fullBodyDay,
    'training-day4': restDay,
    'training-day5': fullBodyDay,
    'training-day6': mobilityDay,
    'training-day7': restDay,
  },
  workouts: [],
};
